import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    ArrowLeft, House as HouseIcon, ExternalLink,
    ChevronRight, Loader2, AlertTriangle, Cpu, MapPinned,
    Building2, Calendar, Wrench, CheckCircle2, Clock, PowerOff, Factory
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;
const DEVICE_TYPE_MAP = {
    'Aksioma01': 'Аксиома-01',
    'Aksioma03': 'Аксиома-03',
    'Karat307': 'Карат-307'
};

const STATUS_META = {
    1: { label: 'Введен в эксплуатацию', badge: 'bg-green-500/15 border-green-500/40 text-green-400', icon: CheckCircle2, iconCls: 'text-green-400' },
    2: { label: 'Ожидание оплаты', badge: 'bg-amber-500/15 border-amber-500/40 text-amber-400', icon: Clock, iconCls: 'text-amber-400' },
    3: { label: 'На поверке', badge: 'bg-blue-500/15 border-blue-500/40 text-blue-400', icon: Wrench, iconCls: 'text-blue-400' },
    4: { label: 'Выведен из эксплуатации', badge: 'bg-slate-600/20 border-slate-600/50 text-slate-400', icon: PowerOff, iconCls: 'text-slate-500' },
};
const DEFAULT_STATUS_META = { label: 'Не указан', badge: 'bg-slate-600/20 border-slate-600/50 text-slate-400', icon: Clock, iconCls: 'text-slate-500' };

const StreetPage = ({
    city,
    street,
    groupName,
    onBack,
    isDesktop = false,
    onSelect,
    onSelectHouse,
    onSelectDevice
}) => {
    const [streetData, setStreetData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedHouses, setExpandedHouses] = useState({});

    // Определяем, является ли группа кастомной
    const isCustomGroup = groupName && groupName !== 'Обслуживаемые' && groupName !== 'Все устройства';

    console.log("groupName = " + groupName);


    useEffect(() => {
        loadStreetData();
    }, [city, street, groupName]);

    const loadStreetData = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            let url = `${API_URL}/api/street/${encodeURIComponent(city)}/${encodeURIComponent(street)}`;
            const params = new URLSearchParams();

            // Передаем groupName на бэкенд только если это кастомная группа
            if (isCustomGroup) {
                params.append('groupName', groupName);
            }

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await axios.get(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setStreetData(response.data || []);
        } catch (err) {
            console.error('❌ Ошибка загрузки данных улицы:', err);
            if (err.response?.status === 401) setError('Необходима авторизация');
            else if (err.response?.status === 404) setStreetData([]);
            else setError('Не удалось загрузить данные улицы');
        } finally {
            setIsLoading(false);
        }
    };

    const toggleHouse = (houseNumber) => {
        setExpandedHouses(prev => ({ ...prev, [houseNumber]: !prev[houseNumber] }));
    };

    const handleDeviceClick = (device, houseNumber) => {
        if (!device) return;
        const deviceTypeRu = DEVICE_TYPE_MAP[device.deviceType] || device.deviceType;
        const payloadDevice = {
            ...device,
            name: device.deviceName,
            deviceType: deviceTypeRu,
            fullName: `${deviceTypeRu} ${device.deviceName || ''}`.trim(),
            city,
            street,
            houseNumber,
            status: 0
        };

        if (onSelect) onSelect({ type: 'device', data: payloadDevice });
        else if (onSelectDevice) onSelectDevice({ type: 'device', data: payloadDevice });
    };

    const handleHouseClick = (houseNumber) => {
        const payload = { type: 'house', data: { city, street, house: `Дом ${houseNumber}` } };
        if (onSelect) onSelect(payload);
        else if (onSelectHouse) onSelectHouse(payload);
    };

    if (isLoading) {
        return (
            <div className="h-full flex items-center justify-center">
                <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="h-full flex items-center justify-center p-4">
                <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            {/* Шапка */}
            <div className="bg-slate-800 border-b border-slate-700 p-4">
                {!isDesktop && (
                    <div className="flex items-center gap-3 mb-3">
                        <button onClick={onBack} className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition text-sm">
                            <ArrowLeft className="w-4 h-4" /> Назад
                        </button>
                    </div>
                )}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="flex items-center gap-2 text-lg sm:text-xl font-bold text-white flex-wrap">
                            <MapPinned className="w-6 h-6 shrink-0 text-cyan-400" />
                            <span>{street}</span>
                        </h2>
                        <p className="text-slate-400 text-sm">{city}</p>
                    </div>
                    <div className="text-slate-400 text-sm">
                        Домов: <span className="text-white font-semibold">{streetData.length}</span>
                    </div>
                </div>
            </div>

            {/* Содержимое */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6">
                {streetData.length === 0 ? (
                    <div className="bg-slate-800 rounded-xl p-12 border border-slate-700 text-center">
                        <Building2 className="w-16 h-16 text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-400">
                            {isCustomGroup
                                ? `В группе «${groupName}» на этой улице нет домов`
                                : 'На этой улице нет домов с устройствами'}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {streetData.map(house => {
                            const houseNumber = house.houseNumber || house.number;
                            const isExpanded = expandedHouses[houseNumber];
                            const nodes = house.meteringNodes || [];
                            const standaloneDevices = house.standaloneDevices || [];
                            const totalDevices = nodes.reduce((acc, n) => acc + (n.heatComputer ? 1 : 0), 0) + standaloneDevices.length;

                            return (
                                <div key={houseNumber} className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                    <div
                                        className="px-4 py-3 bg-slate-700/50 border-b border-slate-700 flex items-center justify-between gap-3 cursor-pointer hover:bg-slate-700/70 transition"
                                        onClick={() => toggleHouse(houseNumber)}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                                            <HouseIcon className="w-5 h-5 text-amber-400 flex-shrink-0" />
                                            <div className="min-w-0">
                                                <div className="text-white font-semibold">Дом {houseNumber}</div>
                                                <div className="text-slate-400 text-xs truncate">
                                                    {nodes.length > 0
                                                        ? `Узлов учёта: ${nodes.length} • Тепловычислителей: ${totalDevices}`
                                                        : standaloneDevices.length > 0
                                                            ? `Тепловычислителей: ${standaloneDevices.length} (без узла учёта)`
                                                            : 'Нет устройств'}
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleHouseClick(houseNumber); }}
                                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-medium transition flex-shrink-0"
                                            title="Открыть страницу дома"
                                        >
                                            <ExternalLink className="w-3.5 h-3.5" />
                                            <span className="hidden sm:inline">Открыть дом</span>
                                        </button>
                                    </div>

                                    {isExpanded && (
                                        <div className="p-3 space-y-4">
                                            {/* Блок 1: Узлы учета (Таблица) */}
                                            {nodes.length > 0 && (
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-sm">
                                                        <thead>
                                                            <tr className="text-slate-400 text-xs border-b border-slate-700">
                                                                <th className="text-left py-2 px-2 font-medium">Узел учёта</th>
                                                                <th className="text-left py-2 px-2 font-medium">Статус</th>
                                                                <th className="text-left py-2 px-2 font-medium">
                                                                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Дата опломбировки</span>
                                                                </th>
                                                                <th className="text-left py-2 px-2 font-medium">
                                                                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Действительно до</span>
                                                                </th>
                                                                <th className="text-left py-2 px-2 font-medium">Тепловычислитель</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {nodes.map(node => {
                                                                const status = node.status || 4;
                                                                const statusMeta = STATUS_META[status] || DEFAULT_STATUS_META;
                                                                const StatusIcon = statusMeta.icon;
                                                                const isCommissioned = status === 1;
                                                                const hc = node.heatComputer;
                                                                const hcDisplayName = hc ? `${DEVICE_TYPE_MAP[hc.deviceType] || hc.deviceType} ${hc.deviceName || ''}`.trim() : '';

                                                                return (
                                                                    <tr key={node.id} className="border-b border-slate-700/50 hover:bg-slate-700/20 transition">
                                                                        <td className="py-2.5 px-2">
                                                                            <div className="text-white font-medium">{node.name}</div>
                                                                            {node.description && <div className="text-slate-500 text-xs truncate max-w-[200px]">{node.description}</div>}
                                                                        </td>
                                                                        <td className="py-2.5 px-2">
                                                                            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[11px] font-medium whitespace-nowrap ${statusMeta.badge}`}>
                                                                                <StatusIcon className={`w-3.5 h-3.5 ${statusMeta.iconCls}`} />
                                                                                {statusMeta.label}
                                                                            </span>
                                                                        </td>
                                                                        <td className="py-2.5 px-2 text-slate-300 whitespace-nowrap">
                                                                            {isCommissioned ? (node.oplombirovanDate || '—') : '—'}
                                                                        </td>
                                                                        <td className="py-2.5 px-2 text-slate-300 whitespace-nowrap">
                                                                            {isCommissioned ? (node.oplombirovanValidUntil || '—') : '—'}
                                                                        </td>
                                                                        <td className="py-2.5 px-2">
                                                                            {hc ? (
                                                                                <button
                                                                                    onClick={() => handleDeviceClick(hc, houseNumber)}
                                                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-600/20 hover:bg-indigo-600/40 text-indigo-300 rounded text-xs transition group border border-indigo-500/30"
                                                                                    title="Открыть тепловычислитель"
                                                                                >
                                                                                    <Cpu className="w-3.5 h-3.5 flex-shrink-0" />
                                                                                    <span className="truncate max-w-[180px]">{hcDisplayName}</span>
                                                                                    <ExternalLink className="w-3 h-3 opacity-60 group-hover:opacity-100 flex-shrink-0" />
                                                                                </button>
                                                                            ) : (
                                                                                <span className="text-slate-500 text-xs italic">Не добавлен</span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}

                                            {/* Блок 2: Standalone устройства (без узла учета) */}
                                            {standaloneDevices.length > 0 && (
                                                <div className="space-y-2">
                                                    {nodes.length === 0 && (
                                                        <div className="flex items-center gap-2 px-2 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                                                            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                                                            <span className="text-amber-300 text-xs">
                                                                Узел учёта не создан, но установлены тепловычислители:
                                                            </span>
                                                        </div>
                                                    )}
                                                    {nodes.length > 0 && (
                                                        <div className="text-slate-400 text-xs font-medium px-1 pt-2">
                                                            Дополнительные устройства (без узла учёта):
                                                        </div>
                                                    )}
                                                    <div className="space-y-1.5">
                                                        {standaloneDevices.map((device, idx) => {
                                                            const deviceTypeRu = DEVICE_TYPE_MAP[device.deviceType] || device.deviceType;
                                                            const displayName = `${deviceTypeRu} ${device.deviceName || ''}`.trim();
                                                            return (
                                                                <button
                                                                    key={device.id || idx}
                                                                    onClick={() => handleDeviceClick(device, houseNumber)}
                                                                    className="w-full flex items-center gap-2.5 px-3 py-2.5 bg-indigo-600/10 hover:bg-indigo-600/25 border border-indigo-500/30 rounded-lg transition group text-left"
                                                                    title="Открыть тепловычислитель"
                                                                >
                                                                    <Cpu className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="text-indigo-200 text-sm font-medium truncate">
                                                                            {displayName || 'Тепловычислитель'}
                                                                        </div>
                                                                        {device.factoryNumber && (
                                                                            <div className="text-slate-500 text-xs">Зав. № {device.factoryNumber}</div>
                                                                        )}
                                                                    </div>
                                                                    <ExternalLink className="w-3.5 h-3.5 text-indigo-400/60 group-hover:text-indigo-300 flex-shrink-0" />
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {nodes.length === 0 && standaloneDevices.length === 0 && (
                                                <div className="text-slate-500 text-sm text-center py-6">
                                                    Узлы учёта и устройства не найдены
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default StreetPage;